export type DashboardQrChallenge = {
  challengeId: string;
  expiresAt: string;
  publicNonce: string;
  type: "artcrew.dashboard.login";
  version: 2;
};

export type DashboardQrStart = {
  challenge: DashboardQrChallenge;
  payload: string;
  pollSecret: string;
};

export type DashboardQrPoll = {
  approvedAccount?: string;
  status: "approved" | "expired" | "pending";
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const OPAQUE_SECRET_PATTERN = /^[A-Za-z0-9_-]{32,256}$/;

export function parseDashboardQrStart(value: unknown, now = Date.now()): DashboardQrStart {
  const root = recordValue(value);
  const challenge = parseChallenge(root?.challenge, now);
  const pollSecret = stringValue(root?.pollSecret);
  const payload = stringValue(root?.payload);
  if (!challenge || !pollSecret || !payload || !OPAQUE_SECRET_PATTERN.test(pollSecret)) {
    throw new Error("Dashboard QR login returned an invalid challenge.");
  }

  const url = new URL(payload);
  if (url.protocol !== "artcrew:" || url.hostname !== "dashboard-login" || url.pathname !== "") {
    throw new Error("Dashboard QR login returned an untrusted payload.");
  }
  const parameterNames = Array.from(url.searchParams.keys());
  if (parameterNames.length !== 1 || parameterNames[0] !== "challenge") {
    throw new Error("Dashboard QR login payload contains unexpected data.");
  }

  let embeddedChallenge: DashboardQrChallenge;
  try {
    embeddedChallenge = parseChallenge(JSON.parse(url.searchParams.get("challenge") ?? ""), now) as DashboardQrChallenge;
  } catch {
    throw new Error("Dashboard QR login payload is malformed.");
  }
  if (!embeddedChallenge || !sameChallenge(challenge, embeddedChallenge) || payload.includes(pollSecret)) {
    throw new Error("Dashboard QR login payload does not match its challenge.");
  }

  return { challenge, payload, pollSecret };
}

export function parseDashboardQrPoll(value: unknown): DashboardQrPoll {
  const root = recordValue(value);
  const status = stringValue(root?.status);
  if (status !== "approved" && status !== "expired" && status !== "pending") {
    throw new Error("Dashboard QR login returned an invalid status.");
  }
  const approvedAccount = stringValue(root?.approvedAccount);
  return approvedAccount ? { approvedAccount, status } : { status };
}

export function requireDashboardQrTarget(value: unknown): "/dashboard/app" {
  if (value !== "/dashboard/app") {
    throw new Error("Dashboard QR login returned an invalid destination.");
  }
  return value;
}

function parseChallenge(value: unknown, now: number): DashboardQrChallenge | null {
  const challenge = recordValue(value);
  const challengeId = stringValue(challenge?.challengeId);
  const expiresAt = stringValue(challenge?.expiresAt);
  const publicNonce = stringValue(challenge?.publicNonce);
  const expiresAtMs = expiresAt ? Date.parse(expiresAt) : Number.NaN;
  if (
    challenge?.type !== "artcrew.dashboard.login"
    || challenge?.version !== 2
    || !challengeId
    || !UUID_PATTERN.test(challengeId)
    || !expiresAt
    || !publicNonce
    || !OPAQUE_SECRET_PATTERN.test(publicNonce)
    || !Number.isFinite(expiresAtMs)
    || expiresAtMs <= now
    || expiresAtMs > now + 10 * 60_000
  ) {
    return null;
  }
  return {
    challengeId,
    expiresAt,
    publicNonce,
    type: "artcrew.dashboard.login",
    version: 2,
  };
}

function sameChallenge(left: DashboardQrChallenge, right: DashboardQrChallenge): boolean {
  return left.challengeId === right.challengeId
    && left.expiresAt === right.expiresAt
    && left.publicNonce === right.publicNonce
    && left.type === right.type
    && left.version === right.version;
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
